import NativeGallery from './NativeGallery/NativeGallery';
import GalleryFullscreen from './GalleryFullscreen';
import './Gallery.scss';
import { sendEvent, getFormattedOptions } from '../helpers/analytics';

const eventAction = 'gallery';

class Gallery {
  constructor(source, id = '#gallery') {
    this.gallery = document.querySelector(id);
    this.source = source;
    this.target = this.gallery?.querySelector('native-gallery');
    if (!this.target || !this.source.length) return;
    this.slides = this.target.children;

    this.withSlideShownEvent = this.gallery?.hasAttribute('data-slide-shown');

    if (this.withSlideShownEvent) {
      this.seenSlides = new Set();
      this.cardRoot = this.gallery?.closest('[data-event-category]');

      if (this.cardRoot) {
        this.eventOptions = this.cardRoot.getAttribute('data-event-options');
      }
    }

    this.load();
  }

  load() {
    const nextButton = this.gallery.querySelector('[data-next]');
    const prevButton = this.gallery.querySelector('[data-prev]');
    this.nativeGallery = new NativeGallery(this.target, {
      onScroll: () => {
        if (nextButton) {
          if (this.nativeGallery.current === this.nativeGallery.count) {
            nextButton.classList.add('-disabled');
          } else {
            nextButton.classList.remove('-disabled');
          }
        }
        if (prevButton) {
          if (this.nativeGallery.currentIndex === 0) {
            prevButton.classList.add('-disabled');
          } else {
            prevButton.classList.remove('-disabled');
          }
        }
      },
    });

    this.addGalleryChangeHandler();

    if (nextButton) {
      nextButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.nativeGallery.next();
      });
    }
    if (prevButton) {
      prevButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.nativeGallery.prev();
      });
    }

    this.fs();
  }

  addGalleryChangeHandler() {
    const bulletsRoot = this.gallery.querySelector('[data-bullets]');
    const bullets = bulletsRoot ? [...bulletsRoot.querySelectorAll('[data-bullet]')] : [];
    const backgroundElement = this.gallery.querySelector('[data-bg]');

    const photosCount = this.target.childElementCount;
    let currentPhotoNumber = 1;

    bullets.forEach((bullet, index) => {
      bullet.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.nativeGallery.scrollToIndex(index);
      });
    });

    this.target.addEventListener('change', (event) => {
      const nextPhotoNumber = event.detail.current;

      if (this.withSlideShownEvent) {
        const slideIndex = nextPhotoNumber;
        // send an event only for first view of each slide (excluding first slide)
        if (slideIndex > 1 && !this.seenSlides.has(slideIndex)) {
          this.seenSlides.add(slideIndex);

          sendEvent(window.eventCategory, 'card_gallery', 'shown', this.eventOptions);

          /**
           *  After the user views 3 unique slides send a page_view event
           *  to prevent a sharp drop in B2B object views
           */
          if (slideIndex === 4) {
            const options = getFormattedOptions(this.eventOptions);
            const buildingId = options?.buildingId || undefined;
            gtag('event', 'page_view', {
              pageType: window.eventCategory,
              page_location: window.location.href,
              send_to: 'G-51QJRHYL1Q',
              backendSplitTestVersion: window.splitTestVersion,
              buildingId,
              ...window.analyticsData,
            });
          }
        }
      }

      if (backgroundElement) {
        backgroundElement.style.background =
          this.source[nextPhotoNumber - 1]?.background || '#d6d6d6';
      }

      if (nextPhotoNumber !== currentPhotoNumber) {
        bullets.forEach((bullet, index) => {
          if (index !== nextPhotoNumber - 1) {
            bullet.classList.remove('-active');
          } else {
            const scrollLeft =
              bullet.offsetLeft + bullet.offsetWidth / 2 - bulletsRoot.offsetWidth / 2;
            try {
              bulletsRoot.scrollTo({
                left: scrollLeft,
                top: 0,
                behavior: 'smooth',
              });
            } catch (err) {
              bulletsRoot.scrollTo(scrollLeft, 0);
            }
            bullet.classList.add('-active');
          }
        });

        if (nextPhotoNumber > currentPhotoNumber) {
          currentPhotoNumber = nextPhotoNumber;
          this.fireGaEvent('next_click');
        } else {
          currentPhotoNumber = nextPhotoNumber;
          this.fireGaEvent('prev_click');
        }
        if (currentPhotoNumber === photosCount) {
          this.fireGaEvent('last_photo');
        }
      }
    });
  }

  fs() {
    const fsButton = this.gallery.querySelector('[data-fs]');
    const video = this.gallery.querySelector('[data-video]');
    const fs = new GalleryFullscreen(this.source, () => {
      if (fsButton) {
        fsButton.addEventListener('click', () => {
          fs.initPsw(this.currentGalleryIndex);
        });
      }

      this.target.addEventListener('click', (event) => {
        if (!(video && video.contains(event.target))) {
          fs.initPsw(this.currentGalleryIndex);
        }
      });
    });
  }

  fireGaEvent(eventLabel) {
    if (!this.withSlideShownEvent) {
      sendEvent(window.eventCategory, eventAction, eventLabel);
    }
  }

  get currentGalleryIndex() {
    return this.nativeGallery.currentImageIndex;
  }
}

export default Gallery;
